import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Archive, ArchiveRestore, Loader2, Pencil, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  adminInputClass,
  filterPillClass,
  filterPillActiveClass,
  StatusPill,
  TableSkeleton,
} from '@/components/admin/AdminTableStyles';
import AccountingShell, {
  AccountingEmpty,
  AccountingError,
} from '@/components/admin/accounting/AccountingShell';
import ConfirmDeleteDialog from '@/components/admin/accounting/ConfirmDeleteDialog';
import BulkDeleteDialog, { BulkDeleteBar } from '@/components/admin/accounting/BulkDeleteDialog';
import useRowSelection from '@/hooks/useRowSelection';
import { accountingError, filtersFromSearch, searchFromFilters } from '@/lib/accounting';
import {
  archiveAccountingItem,
  createAccountingItem,
  deleteAccountingItem,
  getAccountingItems,
  getAccountingTaxes,
  restoreAccountingItem,
  updateAccountingItem,
} from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

const DEFAULT_FILTERS = { q: '', status: 'active', page: '1' };

const STATUS_TABS = [
  { id: 'active', label: 'Active' },
  { id: 'archived', label: 'Archived' },
  { id: 'all', label: 'All' },
];

function emptyItemForm(context) {
  return {
    name: '',
    description: '',
    price: '0.00',
    // The page you are standing on decides the default, because that is
    // almost always what you meant. Both flags are still editable.
    sell_enabled: context === 'sell',
    buy_enabled: context === 'buy',
    tax_ids: [],
  };
}

/**
 * Products & Services, for both Sales and Payments.
 *
 * One page, one collection, one create/edit form — `context` only decides
 * which half of the catalog is listed and which flag a new item gets by
 * default. An item flagged for both shows up here and on the other page, and
 * editing it in either place edits the same record.
 */
export default function AccountingItems({ context }) {
  const { profile } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const [filters, setFilters] = useState(() => filtersFromSearch(location.search, DEFAULT_FILTERS));
  const [rows, setRows] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [pageInfo, setPageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState(() => emptyItemForm(context));
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [deleteFor, setDeleteFor] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  // An item already on an invoice or a bill is archived, never deleted, so it
  // cannot be ticked. That is knowable here, unlike the per-record guards the
  // server applies, so it is settled before anyone selects anything.
  const selection = useRowSelection(rows, {
    keyOf: (row) => row.id,
    canSelect: (row) => !row.in_use,
  });

  useEffect(() => {
    const search = searchFromFilters(filters, DEFAULT_FILTERS);
    if (search !== location.search) {
      navigate({ pathname: location.pathname, search }, { replace: true });
    }
  }, [filters, location.pathname, location.search, navigate]);

  const load = useCallback(
    async ({ quiet } = {}) => {
      if (quiet) setRefreshing(true);
      else setLoading(true);
      setError('');
      try {
        const [list, taxList] = await Promise.all([
          getAccountingItems({
            context,
            q: filters.q || undefined,
            status: filters.status,
            page: Number(filters.page) || 1,
          }),
          getAccountingTaxes(),
        ]);
        setRows(list.data?.items || []);
        setPageInfo(list.data?.page_info || null);
        setTaxes(taxList.data?.items || []);
      } catch (err) {
        setError(accountingError(err, 'Products and services could not be loaded.'));
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [context, filters]
  );

  useEffect(() => {
    load();
  }, [load]);

  const setFilter = (patch) => setFilters((current) => ({ ...current, ...patch, page: '1' }));

  const openCreate = () => {
    setForm(emptyItemForm(context));
    setDialog({ mode: 'create' });
  };

  const openEdit = (item) => {
    setForm({
      name: item.name,
      description: item.description,
      price: (item.price_cents / 100).toFixed(2),
      sell_enabled: item.sell_enabled,
      buy_enabled: item.buy_enabled,
      tax_ids: item.tax_ids || [],
    });
    setDialog({ mode: 'edit', item });
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error('A product or service needs a name.');
      return;
    }
    if (!form.sell_enabled && !form.buy_enabled) {
      toast.error('Tick Sell this, Buy this, or both — otherwise this item can never be used.');
      return;
    }
    const payload = {
      name: form.name,
      description: form.description,
      price: Number(String(form.price).replace(/[$,]/g, '')) || 0,
      sell_enabled: form.sell_enabled,
      buy_enabled: form.buy_enabled,
      tax_ids: form.tax_ids,
    };
    setSaving(true);
    try {
      if (dialog.mode === 'create') {
        await createAccountingItem(payload);
        toast.success('Product or service added.');
      } else {
        await updateAccountingItem(dialog.item.id, payload);
        toast.success('Product or service updated.');
      }
      setDialog(null);
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'It could not be saved.'));
    } finally {
      setSaving(false);
    }
  };

  /**
   * Delete an item nothing has ever used.
   *
   * An item that has reached an invoice or a bill is archived instead — the
   * document snapshotted its name and price and has to keep rendering — and
   * the server refuses those with that reason, which is surfaced as-is rather
   * than translated into something vaguer.
   */
  const confirmDelete = async () => {
    setBusyId(deleteFor.id);
    try {
      await deleteAccountingItem(deleteFor.id);
      toast.success(`${deleteFor.name} deleted.`);
      setDeleteFor(null);
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'That item could not be deleted.'));
    } finally {
      setBusyId('');
    }
  };

  const toggleArchive = async (item) => {
    if (!item.archived && item.in_use) {
      const confirmed = window.confirm(
        `“${item.name}” has been used on a document.\n\nArchiving removes it from the pickers. Invoices and bills that already use it keep their own copy of the name and price, so nothing historical changes.`
      );
      if (!confirmed) return;
    }
    setBusyId(item.id);
    try {
      if (item.archived) {
        await restoreAccountingItem(item.id);
        toast.success(`“${item.name}” restored.`);
      } else {
        await archiveAccountingItem(item.id);
        toast.success(`“${item.name}” archived.`);
      }
      await load({ quiet: true });
    } catch (err) {
      toast.error(accountingError(err, 'That could not be changed.'));
    } finally {
      setBusyId('');
    }
  };

  const isSales = context === 'sell';
  const canManage = profile?.role === 'admin';
  const canArchive = profile?.is_super_admin === true;
  const canDelete = profile?.is_super_admin === true;

  const actions = (
    <>
      <Button type="button" variant="outline" onClick={() => load({ quiet: true })} disabled={refreshing}>
        <RefreshCw className={cn('mr-2 size-4', refreshing && 'animate-spin')} />
        Refresh
      </Button>
      {canManage && (
        <Button type="button" onClick={openCreate} className="bg-[#062B1A] text-white hover:bg-[#041B10]">
          <Plus className="mr-2 size-4" />
          Add a product or service
        </Button>
      )}
    </>
  );

  return (
    <AccountingShell
      title="Products & Services"
      description={
        isSales
          ? 'What the college sells. These are the line items available on an invoice.'
          : 'What the college buys. These are the line items available on a bill.'
      }
      actions={actions}
    >
      <div className="flex flex-wrap items-center gap-2">
        {STATUS_TABS.map((tab) => (
          <Button
            key={tab.id}
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setFilter({ status: tab.id })}
            className={cn(filterPillClass, filters.status === tab.id && filterPillActiveClass)}
          >
            {tab.label}
          </Button>
        ))}
        <div className="relative ml-auto w-full max-w-xs">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#062B1A]/40" />
          <Input
            type="search"
            aria-label="Search products and services"
            value={filters.q}
            onChange={(event) => setFilter({ q: event.target.value })}
            placeholder="Name or description"
            className={cn('pl-9', adminInputClass)}
          />
        </div>
        {filters.q && (
          <Button type="button" variant="ghost" size="sm" onClick={() => setFilter({ q: '' })}>
            <X className="mr-1 size-3.5" />
            Clear
          </Button>
        )}
      </div>

      {canDelete && (
        <BulkDeleteBar
          count={selection.count}
          noun="item"
          onDelete={() => setBulkOpen(true)}
          onClear={selection.clear}
        />
      )}

      {error ? (
        <AccountingError message={error} onRetry={load} />
      ) : loading ? (
        <TableSkeleton rows={5} />
      ) : rows.length === 0 ? (
        <AccountingEmpty
          title={filters.q ? 'Nothing matches this search' : 'No products or services yet'}
          description={
            filters.q
              ? 'Try a different name or description.'
              : isSales
                ? 'Add tuition, fees, scholarships or anything else that belongs on an invoice.'
                : 'Add contracted services, commissions, utilities or anything else that belongs on a bill.'
          }
          action={
            filters.q ? (
              <Button type="button" variant="outline" onClick={() => setFilter({ q: '' })}>
                Clear search
              </Button>
            ) : canManage ? (
              <Button type="button" onClick={openCreate} className="bg-[#062B1A] text-white">
                <Plus className="mr-2 size-4" />
                Add a product or service
              </Button>
            ) : null
          }
        />
      ) : (
        <Card className="overflow-hidden border border-[rgba(6,43,26,0.08)] shadow-sm">
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[910px] text-left text-sm">
              <thead className="bg-[#062B1A] text-[10px] uppercase tracking-[0.035em] text-white">
                <tr>
                  {canDelete && (
                    <th className="w-10 px-3 py-2.5">
                      <Checkbox
                        aria-label="Select every item on this page that can be deleted"
                        className="border-white/50 data-[state=checked]:border-white data-[state=checked]:bg-white data-[state=checked]:text-[#062B1A]"
                        checked={selection.allSelected}
                        disabled={selection.selectableCount === 0}
                        onCheckedChange={selection.toggleAll}
                      />
                    </th>
                  )}
                  <th className="px-3 py-2.5 font-semibold">Name</th>
                  <th className="px-3 py-2.5 font-semibold">Description</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Price</th>
                  <th className="px-3 py-2.5 font-semibold">Tax</th>
                  <th className="px-3 py-2.5 text-right font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[rgba(6,43,26,0.07)]">
                {rows.map((item) => (
                  <tr
                    key={item.id}
                    className={cn('bg-white hover:bg-[#F7F8FA]/80', item.archived && 'opacity-60')}
                  >
                    {canDelete && (
                      <td className="px-3 py-3">
                        <Checkbox
                          aria-label={`Select ${item.name}`}
                          checked={selection.isSelected(item)}
                          disabled={item.in_use}
                          onCheckedChange={() => selection.toggle(item)}
                        />
                      </td>
                    )}
                    <td className="px-3 py-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-[#062B1A]">{item.name}</span>
                        {item.sell_enabled && item.buy_enabled && (
                          <StatusPill tone="gold">Buy &amp; Sell</StatusPill>
                        )}
                        {item.archived && <StatusPill tone="slate">Archived</StatusPill>}
                      </div>
                    </td>
                    <td className="max-w-md px-3 py-3 text-xs text-[#062B1A]/60">
                      {item.description || '—'}
                    </td>
                    <td
                      className={cn(
                        'px-3 py-3 text-right text-sm font-semibold',
                        item.price_cents < 0 ? 'text-emerald-700' : 'text-[#062B1A]'
                      )}
                    >
                      {item.price_display}
                    </td>
                    <td className="px-3 py-3 text-xs text-[#062B1A]/60">{item.tax_display}</td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {canManage && (
                          <Button type="button" variant="outline" size="sm" onClick={() => openEdit(item)}>
                            <Pencil className="mr-1.5 size-3.5" />
                            Edit
                          </Button>
                        )}
                        {canArchive && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={busyId === item.id}
                            onClick={() => toggleArchive(item)}
                          >
                            {busyId === item.id ? (
                              <Loader2 className="mr-1.5 size-3.5 animate-spin" />
                            ) : item.archived ? (
                              <ArchiveRestore className="mr-1.5 size-3.5" />
                            ) : (
                              <Archive className="mr-1.5 size-3.5" />
                            )}
                            {item.archived ? 'Restore' : 'Archive'}
                          </Button>
                        )}
                        {canDelete && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={busyId === item.id}
                            className="text-red-700 hover:bg-red-50"
                            title={
                              item.in_use
                                ? 'This item is on a document already — archive it instead'
                                : 'Delete this item'
                            }
                            onClick={() => setDeleteFor(item)}
                          >
                            <Trash2 className="size-3.5" />
                            <span className="sr-only">Delete</span>
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pageInfo && pageInfo.total_pages > 1 && (
            <div className="flex items-center justify-between border-t border-[rgba(6,43,26,0.07)] bg-white px-4 py-3 text-xs text-[#062B1A]/60">
              <span>{pageInfo.total_count} items</span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageInfo.current_page <= 1}
                  onClick={() => setFilters((current) => ({ ...current, page: String(pageInfo.current_page - 1) }))}
                >
                  Previous
                </Button>
                <span>
                  Page {pageInfo.current_page} of {pageInfo.total_pages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={pageInfo.current_page >= pageInfo.total_pages}
                  onClick={() => setFilters((current) => ({ ...current, page: String(pageInfo.current_page + 1) }))}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      <Dialog open={Boolean(dialog)} onOpenChange={(open) => !open && setDialog(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {dialog?.mode === 'edit' ? 'Edit product or service' : 'Add a product or service'}
            </DialogTitle>
            <DialogDescription>
              One catalog serves both Sales and Payments. What you tick below decides where this
              item can be used.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="item-name">Name</Label>
              <Input
                id="item-name"
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                className={cn('mt-1', adminInputClass)}
              />
            </div>
            <div>
              <Label htmlFor="item-description">Description</Label>
              <Input
                id="item-description"
                value={form.description}
                onChange={(event) => setForm({ ...form, description: event.target.value })}
                className={cn('mt-1', adminInputClass)}
              />
            </div>
            <div>
              <Label htmlFor="item-price">Price</Label>
              <div className="relative mt-1">
                <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-[#062B1A]/45">
                  C$
                </span>
                <Input
                  id="item-price"
                  type="number"
                  step="0.01"
                  value={form.price}
                  onChange={(event) => setForm({ ...form, price: event.target.value })}
                  className={cn('pl-9', adminInputClass)}
                />
              </div>
              <p className="mt-1 text-xs text-[#062B1A]/45">
                A negative price is a credit — use it for scholarships, refunds and pre-deducted
                commission.
              </p>
            </div>

            <fieldset className="space-y-2 rounded-lg border border-[#062B1A]/10 p-3">
              <legend className="px-1 text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                Where it can be used
              </legend>
              <label className="flex items-start gap-3 text-sm text-[#062B1A]">
                <Checkbox
                  checked={form.sell_enabled}
                  onCheckedChange={(checked) => setForm({ ...form, sell_enabled: checked === true })}
                />
                <span>
                  <span className="font-medium">Sell this</span>
                  <span className="block text-xs text-[#062B1A]/50">Selectable on invoices</span>
                </span>
              </label>
              <label className="flex items-start gap-3 text-sm text-[#062B1A]">
                <Checkbox
                  checked={form.buy_enabled}
                  onCheckedChange={(checked) => setForm({ ...form, buy_enabled: checked === true })}
                />
                <span>
                  <span className="font-medium">Buy this</span>
                  <span className="block text-xs text-[#062B1A]/50">Selectable on bills</span>
                </span>
              </label>
            </fieldset>

            <fieldset className="rounded-lg border border-[#062B1A]/10 p-3">
              <legend className="px-1 text-xs font-semibold uppercase tracking-[0.1em] text-[#062B1A]/45">
                Sales tax
              </legend>
              <div className="flex flex-wrap gap-2">
                {taxes.map((tax) => {
                  const on = form.tax_ids.includes(tax.id);
                  return (
                    <button
                      key={tax.id}
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          tax_ids: on
                            ? form.tax_ids.filter((id) => id !== tax.id)
                            : [...form.tax_ids, tax.id],
                        })
                      }
                      className={cn(
                        'rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                        on
                          ? 'border-[#D4AF37] bg-[#062B1A] text-white'
                          : 'border-[#062B1A]/15 bg-white text-[#062B1A]/60 hover:bg-[#062B1A]/5'
                      )}
                    >
                      {tax.label}
                    </button>
                  );
                })}
                {!taxes.length && (
                  <span className="text-xs text-[#062B1A]/45">No taxes configured yet.</span>
                )}
              </div>
            </fieldset>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialog(null)}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={submit}
              disabled={saving}
              className="bg-[#062B1A] text-white hover:bg-[#041B10]"
            >
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <BulkDeleteDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        noun="item"
        rows={selection.selectedRows}
        labelOf={(row) => `${row.name} · ${row.price_display}`}
        remove={(row) => deleteAccountingItem(row.id)}
        onFinished={() => {
          selection.clear();
          load({ quiet: true });
        }}
      />

      <ConfirmDeleteDialog
        open={Boolean(deleteFor)}
        onOpenChange={(next) => !next && setDeleteFor(null)}
        title="Delete this product or service?"
        detail={deleteFor ? `${deleteFor.name} · ${deleteFor.price_display}` : ''}
        consequence={
          deleteFor?.in_use
            ? 'This item has been used on a document. The server will refuse the delete and '
              + 'tell you to archive it instead, so that invoice keeps rendering.'
            : ''
        }
        confirmLabel="Delete item"
        busy={Boolean(busyId)}
        onConfirm={confirmDelete}
      />
    </AccountingShell>
  );
}
